import { z } from 'zod';
import { positiveDecimalSchema, currencyCodeSchema, isoDateSchema } from './common';

export const cardTemplateTypeSchema = z.enum(['income', 'expense', 'benefit', 'todo', 'appointment']);
export type CardTemplateType = z.infer<typeof cardTemplateTypeSchema>;

export const roundingTypeSchema = z.enum(['none', 'nearest_1', 'nearest_5', 'nearest_10', 'nearest_50', 'nearest_100']);
export type RoundingType = z.infer<typeof roundingTypeSchema>;

export const createCardTemplateSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(500).optional(),
  type: cardTemplateTypeSchema,
  color: z.string().max(9).optional(),
  benefitCategory: z.string().max(80).optional(),
  roundingType: roundingTypeSchema.default('none'),
  expiresAt: isoDateSchema.optional(),
  defaultAutoRepeat: z.boolean().default(false),
  defaultIsBalanceCarryover: z.boolean().default(false),
});
export type CreateCardTemplateInput = z.infer<typeof createCardTemplateSchema>;

export const updateCardTemplateSchema = createCardTemplateSchema.partial();
export type UpdateCardTemplateInput = z.infer<typeof updateCardTemplateSchema>;

export const transactionTemplateSubtypeSchema = z.enum(['income', 'expense', 'benefit', 'todo', 'appointment']);
export type TransactionTemplateSubtype = z.infer<typeof transactionTemplateSubtypeSchema>;

export const createTransactionTemplateSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(500).optional(),
  subtype: transactionTemplateSubtypeSchema,
  amount: positiveDecimalSchema.optional(),
  currency: currencyCodeSchema.default('TRY'),
  defaultAutoRepeat: z.boolean().default(false),
  defaultRepeatMonths: z.number().int().min(1).max(120).optional(),
  defaultRepeatInterval: z.number().int().min(1).max(24).optional(),
  defaultIsInstallment: z.boolean().default(false),
  defaultTotalInstallments: z.number().int().min(2).max(120).optional(),
  meta: z.record(z.unknown()).optional(),
  /** 🌐 Topluluk paylaşımı (Phase 32) — create sırasında false default */
  isPublic: z.boolean().default(false),
});
export type CreateTransactionTemplateInput = z.infer<typeof createTransactionTemplateSchema>;

export const updateTransactionTemplateSchema = createTransactionTemplateSchema.partial();
export type UpdateTransactionTemplateInput = z.infer<typeof updateTransactionTemplateSchema>;

/** Public listesi için query — search, subtype filter, sort */
export const listPublicTemplatesQuerySchema = z.object({
  subtype: transactionTemplateSubtypeSchema.optional(),
  search: z.string().max(200).optional(),
  sort: z.enum(['popular', 'recent']).default('popular'),
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(50).default(20),
});
export type ListPublicTemplatesQuery = z.infer<typeof listPublicTemplatesQuerySchema>;

// 🚩 Template moderation (Phase 33) — kullanıcı rapor + admin queue

export const templateReportReasonSchema = z.enum([
  'spam',
  'inappropriate',
  'copyright',
  'misleading',
  'other',
]);
export type TemplateReportReason = z.infer<typeof templateReportReasonSchema>;

export const templateReportStatusSchema = z.enum(['pending', 'dismissed', 'resolved']);
export type TemplateReportStatus = z.infer<typeof templateReportStatusSchema>;

/** User → POST /transaction-templates/:id/report */
export const reportTemplateSchema = z.object({
  reason: templateReportReasonSchema,
  description: z.string().max(1000).optional(),
});
export type ReportTemplateInput = z.infer<typeof reportTemplateSchema>;

/** Admin → GET /admin/template-reports */
export const listTemplateReportsQuerySchema = z.object({
  status: templateReportStatusSchema.optional(),
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(100).default(50),
});
export type ListTemplateReportsQuery = z.infer<typeof listTemplateReportsQuerySchema>;

// 🇪🇺 Subscription cancellation letter generator
export const cancelLetterLocaleSchema = z.enum(['en', 'de', 'fr', 'nl', 'tr']);
export type CancelLetterLocale = z.infer<typeof cancelLetterLocaleSchema>;

export const cancelLetterRequestSchema = z.object({
  subscriptionName: z.string().min(1).max(200),
  customerNumber: z.string().max(80).optional(),
  effectiveDate: isoDateSchema.optional(),
  locale: cancelLetterLocaleSchema,
});
export type CancelLetterRequest = z.infer<typeof cancelLetterRequestSchema>;
